import type { ReactNode } from 'react';

import { cn } from '@/lib/utils';

interface DeckSurfaceProps {
  children: ReactNode;
  /**
   * Padding, radius and the border for the panel.
   *
   * The border is the caller's because the shape differs: the overview and the
   * page headers are bordered panels, while the feature tracker's identity
   * strip is a card's head and carries a bottom rule only. The *paint* — the
   * tinted sheet and the light on it — is not yours to change.
   */
  className?: string;
}

/**
 * The platform console's banner surface.
 *
 * A soft accent sheet with three layers of light over it: two blurred orbs
 * drifting out of step and a slow sweep across the panel. Every page of the
 * console that opens with a banner uses this, so the overview and the lists
 * cannot end up lit differently from each other.
 *
 * The same band the teacher console opens on (`ClassSelectorHeader`) and the
 * same decoration the institute console's headings carry — our own staff
 * console used to be near-black chrome of its own, which read as a different
 * product from the one it administers. Painted from `accent-soft` /
 * `accent-line` rather than a fixed tint, so it follows the theme like every
 * other panel (CLAUDE.md §7).
 *
 * None of the decoration carries meaning. All of it is hidden from assistive
 * technology and held still under `prefers-reduced-motion` (see index.css).
 *
 * The layers are painted before the children and neither is given a z-index:
 * the children come later in the document and are positioned, so they sit on
 * top by document order alone. Negative z-index against an `isolate` parent is
 * the sort of thing that breaks the first time somebody adds a dropdown.
 */
export function DeckSurface({ children, className }: DeckSurfaceProps) {
  return (
    // A plain `div`: this is paint, not structure. Whatever heading or landmark
    // the panel deserves is the caller's to put inside it.
    <div className={cn('relative isolate overflow-hidden bg-accent-soft', className)}>
      <span
        aria-hidden
        className="pointer-events-none absolute -left-24 -top-28 h-64 w-64 animate-orb rounded-full bg-gradient-to-br from-blue-400/30 to-indigo-500/20 blur-3xl"
      />
      <span
        aria-hidden
        className="pointer-events-none absolute -bottom-28 right-1/4 h-56 w-56 animate-orb-slow rounded-full bg-gradient-to-br from-sky-300/25 to-blue-400/10 blur-3xl"
      />
      <span aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden">
        <span className="absolute inset-y-0 left-0 w-1/4 animate-sheen bg-gradient-to-r from-transparent via-white/50 to-transparent dark:via-white/10" />
      </span>

      <div className="relative">{children}</div>
    </div>
  );
}
